import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api/client";
import { useAuthStore } from "../store/authStore";
import { Button } from "../components/ui/Button";
import { TextInput } from "../components/ui/Field";

type Step = "identity" | "otp";

export function SignInScreen() {
  const navigate = useNavigate();
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const [step, setStep] = useState<Step>("identity");
  const [mode, setMode] = useState<"email" | "phone">("phone");
  const [identity, setIdentity] = useState("");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  async function startOtp() {
    setError(null);
    setBusy(true);
    try {
      const body =
        mode === "email"
          ? { email: identity.trim() }
          : { phone: identity.trim() };
      await apiFetch("/api/v1/auth/otp/start", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setStep("otp");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start OTP");
    } finally {
      setBusy(false);
    }
  }

  async function verify(code: string) {
    if (code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const body =
        mode === "email"
          ? { email: identity.trim(), code }
          : { phone: identity.trim(), code };
      await apiFetch("/api/v1/auth/otp/verify", {
        method: "POST",
        body: JSON.stringify(body),
      });
      await refreshMe();
      navigate("/check", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid code");
      setDigits(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  }

  function onDigit(i: number, value: string) {
    const v = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
    const code = next.join("");
    if (code.length === 6) void verify(code);
  }

  return (
    <div className="auth-wrap">
      <div className="card auth-card stack">
        <h1 className="screen-title">Sign in</h1>
        {step === "identity" ? (
          <>
            <div className="row">
              <Button
                variant={mode === "phone" ? "primary" : "ghost"}
                onClick={() => setMode("phone")}
              >
                Phone
              </Button>
              <Button
                variant={mode === "email" ? "primary" : "ghost"}
                onClick={() => setMode("email")}
              >
                Email
              </Button>
            </div>
            <TextInput
              label={mode === "phone" ? "Phone" : "Email"}
              placeholder={mode === "phone" ? "+1…" : "you@…"}
              value={identity}
              onChange={(e) => setIdentity(e.target.value)}
              autoComplete={mode === "phone" ? "tel" : "email"}
              inputMode={mode === "phone" ? "tel" : "email"}
            />
            <Button
              variant="primary"
              disabled={busy || !identity.trim()}
              onClick={() => void startOtp()}
            >
              Send code
            </Button>
          </>
        ) : (
          <>
            <p className="muted">Enter the 6-digit code</p>
            <div className="otp-boxes">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    refs.current[i] = el;
                  }}
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  aria-label={`Digit ${i + 1}`}
                  onChange={(e) => onDigit(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !digits[i] && i > 0) {
                      refs.current[i - 1]?.focus();
                    }
                  }}
                />
              ))}
            </div>
            <Button variant="ghost" disabled={busy} onClick={() => setStep("identity")}>
              Back
            </Button>
          </>
        )}
        {error ? <p className="legal-flag">{error}</p> : null}
      </div>
    </div>
  );
}
